import * as React from "react"
import { cn } from "../../../lib/utils"
import Button from "../Button/Button"

export interface DevErrorProps {
  /**
   * The error object
   */
  error?: Error | null
  /**
   * Error information from React Router or error boundaries
   */
  errorInfo?: {
    componentStack?: string
  } | null
  /**
   * Reset function to retry rendering
   */
  resetError?: () => void
  /**
   * Additional class names for the container
   */
  className?: string
}

function DevError({
  error,
  errorInfo,
  resetError,
  className,
}: DevErrorProps) {
  const [showDetails, setShowDetails] = React.useState(true)
  const isDev = import.meta.env?.DEV ?? false

  const handleReload = () => {
    window.location.reload()
  }

  const handleGoBack = () => {
    window.history.back()
  }

  const handleCopyError = () => {
    const errorText = `
Error: ${error?.message || "Unknown error"}

Stack Trace:
${error?.stack || "No stack trace available"}

Component Stack:
${errorInfo?.componentStack || "No component stack available"}
    `.trim()

    navigator.clipboard.writeText(errorText)
  }

  return (
    <div
      className={cn(
        "min-h-screen bg-background flex items-center justify-center p-4",
        className
      )}
    >
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center size-12 rounded-full bg-destructive/10">
            <svg
              className="size-6 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {isDev ? "Development Error" : "Something went wrong"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isDev
                ? "An error occurred during rendering"
                : "We're sorry, an unexpected error has occurred"}
            </p>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-4">
          <p className="text-destructive font-mono text-sm break-all">
            {error?.message || "An unknown error occurred"}
          </p>
        </div>

        {/* Stack Trace (Dev only) */}
        {isDev && (error?.stack || errorInfo?.componentStack) && (
          <div className="mb-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <svg
                className={cn(
                  "size-4 transition-transform",
                  showDetails && "rotate-90"
                )}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              {showDetails ? "Hide" : "Show"} Error Details
            </button>

            {showDetails && (
              <div className="space-y-3">
                {error?.stack && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Stack Trace
                    </p>
                    <pre className="bg-muted/50 border rounded-lg p-3 text-xs font-mono text-foreground overflow-x-auto max-h-48 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Component Stack
                    </p>
                    <pre className="bg-muted/50 border rounded-lg p-3 text-xs font-mono text-foreground overflow-x-auto max-h-48 overflow-y-auto">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {resetError && (
            <Button onClick={resetError} variant="default">
              Try Again
            </Button>
          )}
          <Button onClick={handleReload} variant="outline">
            Reload Page
          </Button>
          <Button onClick={handleGoBack} variant="outline">
            Go Back
          </Button>
          {isDev && (
            <Button onClick={handleCopyError} variant="ghost">
              <svg
                className="size-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy Error
            </Button>
          )}
        </div>

        {/* Dev Tips */}
        {isDev && (
          <div className="mt-6 p-4 bg-muted/30 border rounded-lg">
            <p className="text-sm font-medium text-foreground mb-2">
              Debugging Tips
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>Check the browser console for more details</li>
              <li>Review the component stack to find the error source</li>
              <li>Verify that all props are being passed correctly</li>
              <li>Check for any async operations that may have failed</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

DevError.displayName = "DevError"

export { DevError }
